/**
 * Voiceprint settings card — minimal, clinician-terse UX.
 *
 * Default state shows a one-line status: "Voice enrolled" OR "Not enrolled yet".
 * Expanding reveals:
 *   - a 30 s record button (auto-stops)
 *   - a consent checkbox
 *   - an enroll button (disabled until both conditions are met)
 *   - a list of past enrollments with a revoke action per row
 *
 * Keeps clear of AI-metric dashboards per feedback memory
 * `feedback_clinician_ui_density.md`.
 */

import React, { useMemo, useState } from 'react';
import {
  AlertCircle,
  Check,
  Mic,
  RotateCcw,
  ShieldCheck,
  Square,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from '../../hooks/useTranslation';
import { useAiAvailability } from '@/config/aiCapabilities';
import { useDateFormat } from '@/hooks/useDateFormat';
import { useVoiceprintRecorder } from '../../hooks/useVoiceprintRecorder';
import {
  useEnrollVoiceprint,
  useMyVoiceprints,
  useRevokeVoiceprint,
} from '../../hooks/useVoiceprints';
import type { Voiceprint } from '../../services/voiceprintApi';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';

interface Props {
  role?: 'doctor' | 'nurse' | 'patient';
}

const MAX_SECONDS = 30;
const MIN_SECONDS = 8;

export const VoiceprintSettingsCard: React.FC<Props> = ({ role = 'doctor' }) => {
  const { t } = useTranslation();
  const enrolmentAvailable = useAiAvailability('voiceprints').available;
  const [expanded, setExpanded] = useState(false);
  const [consent, setConsent] = useState(false);
  const [enrollError, setEnrollError] = useState<string | null>(null);

  const { data: voiceprints, isLoading: listLoading } = useMyVoiceprints();
  const enroll = useEnrollVoiceprint();
  const revoke = useRevokeVoiceprint();
  const recorder = useVoiceprintRecorder({ maxSeconds: MAX_SECONDS });

  const activeForRole = useMemo(
    () => voiceprints?.find((v) => v.role === role && v.isActive) ?? null,
    [voiceprints, role],
  );
  const historyForRole = useMemo(
    () => voiceprints?.filter((v) => v.role === role && !v.isActive) ?? [],
    [voiceprints, role],
  );

  const canSubmit =
    recorder.state === 'ready' &&
    !!recorder.captured &&
    recorder.captured.durationSec >= MIN_SECONDS &&
    consent;

  const secondsRecorded = Math.min(
    MAX_SECONDS,
    Math.floor(recorder.elapsedMs / 1000),
  );

  const handleSubmit = async () => {
    setEnrollError(null);
    if (!recorder.captured) return;
    try {
      await enroll.mutateAsync({
        audio: recorder.captured.wavBase64,
        role,
        sampleDurationSec: recorder.captured.durationSec,
        consentAccepted: true,
        replaceActive: !!activeForRole,
      });
      toast.success(
        activeForRole
          ? t('voiceprint.replacedToast')
          : t('voiceprint.savedToast'),
        { description: t('voiceprint.savedToastDesc') },
      );
      recorder.reset();
      setConsent(false);
      setExpanded(false);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      setEnrollError(e.response?.data?.message || t('voiceprint.errors.enroll'));
    }
  };

  return (
    <section
      className="rounded-2xl border"
      style={{
        backgroundColor: 'var(--color-bg-primary)',
        borderColor: 'var(--color-border-primary)',
      }}
    >
      <header
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 px-5 sm:px-6 pt-5 pb-4 border-b"
        style={{ borderColor: 'var(--color-border-primary)' }}
      >
        <div className="min-w-0">
          <div
            className="inline-flex items-center gap-2"
            style={{ color: 'var(--color-text-tertiary)' }}
          >
            <span
              aria-hidden
              className="w-6 h-px"
              style={{ backgroundColor: 'var(--color-beige-500)' }}
            />
            <span className="text-[10px] uppercase tracking-[0.25em] font-semibold">
              {(() => {
                const eb = t('voiceprint.eyebrow');
                return eb && eb !== 'voiceprint.eyebrow' ? eb : 'Voice';
              })()}
            </span>
          </div>
          <h3
            className="mt-1.5 text-base sm:text-lg font-semibold tracking-tight flex items-center gap-2"
            style={{ color: 'var(--color-text-primary)' }}
          >
            <Mic
              className="w-4 h-4"
              style={{ color: 'var(--color-primary-700)' }}
            />
            {t('voiceprint.title')}
          </h3>
        </div>
        <span
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.25em] font-semibold whitespace-nowrap flex-shrink-0 self-start sm:self-auto"
          style={{
            border: '1px solid var(--color-border-primary)',
            color: activeForRole
              ? 'var(--color-primary-700)'
              : 'var(--color-text-tertiary)',
            backgroundColor: 'var(--color-bg-primary)',
          }}
        >
          <span
            className="w-1.5 h-1.5 rounded-full"
            style={{
              backgroundColor: activeForRole
                ? 'var(--color-primary-600)'
                : 'var(--color-text-tertiary)',
            }}
          />
          {activeForRole
            ? t('voiceprint.enrolled')
            : t('voiceprint.notEnrolled')}
        </span>
      </header>

      <div className="px-5 sm:px-6 py-5 space-y-4">

      {!enrolmentAvailable ? (
        // The embedding comes from the self-hosted speech service; when the
        // clinic is routed elsewhere the enrol call is refused, so say why
        // instead of offering a button that fails.
        <p className="text-sm" style={{ color: 'var(--color-text-tertiary)' }}>
          {t('voiceprint.unavailable')}
        </p>
      ) : !expanded ? (
        <Button
          variant="link"
          type="button"
          onClick={() => setExpanded(true)}
        >
          {activeForRole
            ? t('voiceprint.replaceCta')
            : t('voiceprint.enrollCta')}
        </Button>
      ) : (
        <div className="space-y-4">
          {/* --- Suggested prompts --- */}
          <div
            className="rounded-xl p-4"
            style={{
              backgroundColor: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border-primary)',
            }}
          >
            <div className="flex items-start justify-between gap-2">
              <span
                className="text-sm font-semibold"
                style={{ color: 'var(--color-text-primary)' }}
              >
                {t('voiceprint.promptHeading')}
              </span>
            </div>
            <p
              className="mt-1 text-xs"
              style={{ color: 'var(--color-text-tertiary)' }}
            >
              {t('voiceprint.promptHint')}
            </p>
            <ol
              className="mt-3 space-y-2 text-sm leading-snug"
              style={{ color: 'var(--color-text-primary)' }}
            >
              {[1, 2, 3].map((n) => (
                <li key={n} className="flex items-start gap-2">
                  <span
                    className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-semibold"
                    style={{
                      backgroundColor: 'var(--color-primary-50)',
                      color: 'var(--color-primary-600)',
                    }}
                  >
                    {n}
                  </span>
                  <span>{t(`voiceprint.prompt${n}`)}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* --- Recorder --- */}
          <div
            className="rounded-xl p-4 border"
            style={{ borderColor: 'var(--color-border, #e5e7eb)' }}
          >
            <div className="flex items-center justify-between mb-3">
              <span
                className="text-sm font-medium"
                style={{ color: 'var(--color-text-primary)' }}
              >
                {recorder.state === 'recording'
                  ? t('voiceprint.recordingNow')
                  : recorder.state === 'processing'
                    ? t('voiceprint.processing')
                    : t('voiceprint.captureLabel', {
                        seconds: MAX_SECONDS,
                      } as unknown as Record<string, string>)}
              </span>
              {recorder.state === 'recording' && (
                <span className="text-xs font-mono tabular-nums">
                  {secondsRecorded}s / {MAX_SECONDS}s
                </span>
              )}
            </div>

            {recorder.state === 'idle' || recorder.state === 'error' ? (
              <Button
                type="button"
                onClick={recorder.start}
              >
                <Mic />
                {t('voiceprint.startRecording')}
              </Button>
            ) : null}

            {recorder.state === 'requesting_mic' ||
            recorder.state === 'processing' ? (
              <Button
                variant="secondary"
                type="button"
                disabled
                loading
              >
                {recorder.state === 'requesting_mic'
                  ? t('voiceprint.waitingForMic')
                  : t('voiceprint.processing')}
              </Button>
            ) : null}

            {recorder.state === 'recording' && (
              <Button
                variant="destructive"
                type="button"
                onClick={recorder.stop}
              >
                <Square />
                {t('voiceprint.stopRecording')}
              </Button>
            )}

            {recorder.state === 'ready' && recorder.captured && (
              <div className="flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1 text-sm">
                  <Check className="w-4 h-4 text-green-600" />
                  {t('voiceprint.capturedSeconds', {
                    seconds: recorder.captured.durationSec,
                  } as unknown as Record<string, string>)}
                </span>
                <Button
                  variant="link"
                  size="sm"
                  type="button"
                  onClick={recorder.reset}
                >
                  <RotateCcw />
                  {t('voiceprint.recordAgain')}
                </Button>
              </div>
            )}

            {recorder.error && (
              <p className="mt-2 flex items-center gap-1 text-xs text-red-600">
                <AlertCircle className="w-4 h-4" />
                {recorder.error}
              </p>
            )}

            {recorder.state === 'ready' &&
              recorder.captured &&
              recorder.captured.durationSec < MIN_SECONDS && (
                <p className="mt-2 text-xs text-amber-700">
                  {t('voiceprint.tooShort', {
                    min: MIN_SECONDS,
                  } as unknown as Record<string, string>)}
                </p>
              )}
          </div>

          {/* --- Consent --- */}
          <Checkbox
            checked={consent}
            onCheckedChange={setConsent}
            label={
              <span className="flex-1">
                <ShieldCheck className="w-4 h-4 inline-block me-1 -mt-0.5" />
                {t('voiceprint.consent')}
              </span>
            }
          />

          {/* --- Submit --- */}
          <div className="flex items-center gap-3 flex-wrap">
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              loading={enroll.isPending}
            >
              {!enroll.isPending && <Check />}
              {activeForRole
                ? t('voiceprint.submitReplace')
                : t('voiceprint.submitEnroll')}
            </Button>
            <Button
              variant="link"
              size="sm"
              type="button"
              onClick={() => {
                recorder.reset();
                setExpanded(false);
                setConsent(false);
                setEnrollError(null);
              }}
              className="text-[var(--color-text-secondary)]"
            >
              {t('common.cancel')}
            </Button>
            {enrollError && (
              <p className="basis-full flex items-center gap-1 text-xs text-red-600">
                <AlertCircle className="w-4 h-4" />
                {enrollError}
              </p>
            )}
          </div>
        </div>
      )}

      {/* --- History --- */}
      {!listLoading && (activeForRole || historyForRole.length > 0) && (
        <div className="mt-5 pt-4 border-t">
          <h4
            className="text-xs font-semibold uppercase tracking-wide mb-2"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            {t('voiceprint.history')}
          </h4>
          <ul className="space-y-1.5">
            {activeForRole && (
              <VoiceprintRow
                vp={activeForRole}
                onRevoke={async (id) => {
                  await revoke.mutateAsync(id);
                }}
                revoking={revoke.isPending}
              />
            )}
            {historyForRole.map((vp) => (
              <VoiceprintRow key={vp.id} vp={vp} />
            ))}
          </ul>
        </div>
      )}
      </div>
    </section>
  );
};

interface RowProps {
  vp: Voiceprint;
  onRevoke?: (id: string) => Promise<void>;
  revoking?: boolean;
}

const VoiceprintRow: React.FC<RowProps> = ({ vp, onRevoke, revoking }) => {
  const { t } = useTranslation();
  const { formatDateTime } = useDateFormat();
  const when = formatDateTime(vp.enrolledAt);
  return (
    <li
      className="flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm"
      style={{ backgroundColor: 'var(--color-bg-secondary, #f9fafb)' }}
    >
      <div className="flex items-center gap-2 min-w-0">
        {vp.isActive ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
            <Check className="w-3 h-3" />
            {t('voiceprint.rowActive')}
          </span>
        ) : (
          <span
            className="text-xs font-medium"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            {t('voiceprint.rowRetired')}
          </span>
        )}
        <span
          className="truncate"
          style={{ color: 'var(--color-text-primary)' }}
        >
          {when}
        </span>
      </div>
      {vp.isActive && onRevoke && (
        <Button
          variant="ghost"
          size="xs"
          type="button"
          onClick={() => onRevoke(vp.id)}
          loading={revoking}
          className="text-red-600 hover:text-red-700"
        >
          {!revoking && <Trash2 />}
          {t('voiceprint.revoke')}
        </Button>
      )}
    </li>
  );
};
